import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trash2, CheckCircle2, AlertTriangle } from "lucide-react";
import { AuthScene, Button, Loader } from "@wa/ui";
import type { AuthLayoutSpec } from "@wa/core/theme";
import { useT } from "../i18n/I18nProvider";
import { accountDeleteTokenStatus, confirmAccountDeletion } from "../lib/auth";
import { Logo } from "../components/Logo";
import { AppBackground } from "../components/AppBackground";
import { LanguageMenu } from "../components/LanguageMenu";

/**
 * Konto kustutamise kinnitusleht - paroolita (OAuth/Google) kasutaja voo TEINE
 * samm. Kinnituskirja lingi (`/confirm-delete?token=…`) siht. Sessioonist
 * sõltumatu (võib avaneda välja logituna / muul seadmel). Kinnitus on selgesõnaline
 * PUNANE nupp (POST), et e-kirja-skännerite eelnõudmine (GET) ei kustutaks kogemata.
 */
export function ConfirmDeletePage({
  onDone,
  authSpec,
}: {
  onDone: () => void;
  authSpec?: AuthLayoutSpec;
}) {
  const t = useT();

  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const [status, setStatus] = useState<"checking" | "valid" | "invalid" | "done">("checking");
  const [busy, setBusy] = useState(false);
  const [graceDays, setGraceDays] = useState(30);

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    accountDeleteTokenStatus(token).then((valid) => setStatus(valid ? "valid" : "invalid"));
  }, [token]);

  async function confirm() {
    setBusy(true);
    try {
      const res = await confirmAccountDeletion(token);
      if (res.gracePeriodDays) setGraceDays(res.gracePeriodDays);
      setStatus("done");
    } catch {
      // Ainuke sisuline viga on aegunud/kasutatud token → näita kehtetuse-vaadet.
      setStatus("invalid");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthScene
      layout={authSpec?.layout}
      background={<AppBackground spec={authSpec} />}
      logo={<Logo variant="full" className="h-11 w-auto text-foreground md:h-12" />}
      topRight={<LanguageMenu />}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="card-aurora-border rounded-xl bg-card/95 p-6 shadow-soft">
          {status === "checking" ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <Loader size="md" />
              <p className="text-sm text-muted-foreground">{t("confirmDelete.checking")}</p>
            </div>
          ) : status === "invalid" ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <AlertTriangle className="size-8 text-destructive" />
              <h1 className="text-xl font-bold tracking-tight">{t("confirmDelete.title")}</h1>
              <p className="text-sm text-destructive">{t("confirmDelete.invalid")}</p>
              <Button type="button" variant="outline" size="lg" className="w-full" onClick={onDone}>
                {t("confirmDelete.continue")}
              </Button>
            </div>
          ) : status === "done" ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <CheckCircle2 className="size-8 text-primary" />
              <h1 className="text-xl font-bold tracking-tight">{t("confirmDelete.doneTitle")}</h1>
              <p className="text-sm text-muted-foreground">{t("confirmDelete.done", { days: graceDays })}</p>
              <Button type="button" variant="outline" size="lg" className="w-full" onClick={onDone}>
                {t("confirmDelete.continue")}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <Trash2 className="size-8 text-destructive" />
              <h1 className="text-xl font-bold tracking-tight">{t("confirmDelete.title")}</h1>
              <p className="text-sm text-muted-foreground">{t("confirmDelete.confirm")}</p>
              <Button type="button" variant="destructive" size="lg" loading={busy} className="w-full" onClick={confirm}>
                {t("confirmDelete.confirmButton")}
              </Button>
              <Button type="button" variant="ghost" size="sm" className="w-full" onClick={onDone} disabled={busy}>
                {t("common.cancel")}
              </Button>
            </div>
          )}
        </div>
      </motion.div>
    </AuthScene>
  );
}
